import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const agents = [
  { label: 'Cloud Architect Agent', path: '/agents/cloud-architect', desc: 'Architecture design and review for AWS, Azure, GCP, and hybrid environments.' },
  { label: 'CPA Agent', path: '/agents/cpa', desc: 'IFRS/GAAP accounting automation, reconciliations, and financial reporting.' },
  { label: 'Data Engineer Agent', path: '/agents/data-engineering', desc: 'Pipeline design, schema governance, and data platform modernisation.' },
  { label: 'Interview Coach', path: '/agents/interview-agent', desc: 'Automated interview question generation, scoring, and candidate feedback.' },
  { label: 'Recruiter Agent', path: '/agents/recruiter-agent', desc: 'Screening, shortlisting, and recruiter workflow automation.' },
  { label: 'SDR Agent', path: '/agents/sdr-agent', desc: 'Sales prospecting, outreach automation, and pipeline building.' },
  { label: 'Governance Audit Agent', path: '/agents/governance-audit', desc: 'Policy-as-code governance and compliance audit automation.' },
  { label: 'KYC Onboarding Agent', path: '/agents/kyc-onboarding', desc: 'Document-based identity verification and KYC workflow automation.' },
  { label: 'LLM Judge Agent', path: '/agents/llm-as-judge', desc: 'AI output evaluation and quality assessment for enterprise GenAI pipelines.' },
];

const faqs = [
  {
    q: 'Can Eagentix AI agents be deployed within Singapore?',
    a: 'Yes. Eagentix supports private cloud and on-premises deployment within Singapore, ensuring data sovereignty and compliance with MAS guidelines and Singapore\'s Personal Data Protection Act (PDPA).',
  },
  {
    q: 'Are Eagentix agents suitable for MAS-regulated financial institutions in Singapore?',
    a: 'Yes. Eagentix agents are designed with enterprise governance, audit trails, and policy-as-code guardrails appropriate for MAS-regulated environments.',
  },
  {
    q: 'What AI agents are available for Singapore enterprises?',
    a: 'Eagentix offers over 19 purpose-built agents including Cloud Architect, CPA, Data Engineer, Interview Coach, Recruiter, SDR, and more — all deployable within Singapore infrastructure.',
  },
  {
    q: 'Does Eagentix support the Singapore Smart Nation initiative?',
    a: 'Eagentix AI agents align with Smart Nation digital transformation goals by automating enterprise workflows, reducing operational costs, and enabling AI-first operations at scale.',
  },
  {
    q: 'How quickly can Singapore enterprises deploy Eagentix agents?',
    a: 'Enterprises can get started within days via the Eagentix cloud platform or arrange private deployment within their own Singapore-based infrastructure.',
  },
];

export default function SingaporeAIAgents() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };
  const cardDark = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'rgba(255,255,255,0.06)', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Enterprise AI Agents for Singapore Businesses | Eagentix"
        description="Eagentix provides purpose-built enterprise AI agents for Singapore businesses — deployed privately, governed for MAS compliance, and built for Smart Nation environments."
        pathname="/industries/singapore-enterprise-ai-agents"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Pillar Page · Singapore AI Ecosystem
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Consulting & Agents in Singapore
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix is a leading AI consulting firm headquartered in Singapore, dedicated to building 
              sovereign, secure, and MAS-compliant enterprise AI agents for the regional digital economy.
            </p>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Singapore Enterprises Choose Eagentix */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px' }}>
                Why Eagentix in Singapore?
              </h2>
              <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                We bridge the gap between global frontier AI and Singapore's specific regulatory requirements. Our team brings decades of experience in enterprise architecture and strategic consulting, headquartered right here in the heart of the regional digital economy.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Headquartered in International Plaza, Singapore',
                  'Expertise in MAS Technology Risk Management (TRM) guidelines',
                  'Alignment with Singapore PDPA and Model AI Governance Framework',
                  'Deep experience with Smart Nation digital infrastructure',
                  'Private & Sovereign deployment for local regulated sectors'
                ].map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: '#1d1d1f', fontWeight: 500 }}>
                    <span style={{ color: '#10b981' }}>✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: '#f1f5f9', borderRadius: '24px', padding: '40px', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h4 style={{ color: '#0071e3', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.1em', marginBottom: '16px', fontWeight: 700 }}>
                Safe AI for Smart Nation
              </h4>
              <p style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
                We follow a safety-first methodology for all local deployments, ensuring your AI transformation is robust and compliant.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Deterministic Guardrails</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Human-in-the-Loop Verification</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Immutable Audit Trails</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Air-Gapped Ready Architecture</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available agents */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Available AI Agents for Singapore Businesses
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Over 19 purpose-built agents covering cloud, finance, data, HR, and sales — all deployable within Singapore infrastructure.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {agents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block', transition: 'box-shadow 0.2s' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries served */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Industries Served in Singapore
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix serves enterprise customers across Singapore's key sectors.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {[
              { industry: 'Financial Services', desc: 'Banks, insurers, and fintechs using agents for KYC, reconciliations, reporting, and compliance automation.' },
              { industry: 'Professional Services', desc: 'Consulting, legal, and accounting firms using agents for document processing, research, and workflow automation.' },
              { industry: 'Technology', desc: 'Cloud teams, ISVs, and platform engineering groups using agents for architecture review, data engineering, and DevOps.' },
              { industry: 'HR & Recruitment', desc: 'HR teams and recruitment firms using agents for interview automation, candidate screening, and recruiter workflows.' },
            ].map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>{item.industry}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ ...sectionDark }}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready to Deploy AI Agents in Singapore?
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Start with a strategy session. Our Singapore-based team will help you identify the highest-value automation opportunities.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
